import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Inventory as InventoryIcon,
    ExpandMore as ExpandMoreIcon,
    ChevronRight as ChevronRightIcon,
    Search as SearchIcon,
    AddCircleOutline as AddChildIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import CategoryDialog from './CategoryDialog';

const CategoryManagement = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [filterParentOnly, setFilterParentOnly] = useState(false);
    
    // Dialog states
    const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
    
    // Menu states
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);

    const fetchCategories = async () => {
        try {
            const response = await api.get('/products/categories');
            setCategories(response.data.categories || []);
            setError('');
        } catch (error) {
            setCategories([]);
            setError('Failed to fetch categories. Please try again.');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchCategories();
            setLoading(false);
        };
        loadData();
    }, []);

    const handleExpandCollapse = (categoryId) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const handleCategoryAction = (action, category = null) => {
        setError('');
        setSuccess('');
        setCategoryDialog({ open: true, category });
        setMenuAnchor(null);
    };
    
    const handleDialogClose = (successMessage = null) => {
        setCategoryDialog({ open: false, category: null });
        setError('');
        
        if (successMessage) {
            setSuccess(successMessage);
            // Only fetch categories if there was a successful operation
            fetchCategories();
        }
    };
    
    const handleDelete = async () => {
        try {
            await api.delete(`/products/categories/${deleteDialog.category.id}`);
            
            // Show success message
            setError('');
            const categoryName = deleteDialog.category.name;
            setSuccess(`Category "${categoryName}" deleted successfully!`);
            setDeleteDialog({ open: false, category: null });
            await fetchCategories();
            
        } catch (error) {
            // Extract error message from response
            const errorMessage = error.response?.data?.message || 'Failed to delete category. Please try again.';
            setError(errorMessage);
        }
    };
    
    const handleAddProduct = () => {
        navigate('/app/products');
    };

    const handleAddChild = (parentCategory) => {
        setCategoryDialog({ open: true, category: null, parent: parentCategory });
        setMenuAnchor(null);
    };

    const filterCategories = (categories) => {
        return categories.filter(category => {
            const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                (category.description || '').toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = !filterParentOnly || !category.parent_id;
            return matchesSearch && matchesFilter;
        });
    };

    const processCategories = (categories) => {
        // First, filter categories
        const filteredCategories = filterCategories(categories);
        
        // Then organize by parent_id
        const categoryMap = new Map();
        const rootCategories = [];
        
        filteredCategories.forEach(category => {
            categoryMap.set(category.id, {
                ...category,
                children: [],
                level: 0
            });
        });
        
        filteredCategories.forEach(category => {
            const processedCategory = categoryMap.get(category.id);
            if (category.parent_id) {
                const parent = categoryMap.get(category.parent_id);
                if (parent) {
                    parent.children.push(processedCategory);
                    processedCategory.level = parent.level + 1;
                } else {
                    rootCategories.push(processedCategory);
                }
            } else {
                rootCategories.push(processedCategory);
            }
        });
        
        // Flatten the tree based on expanded state
        const flattenedRows = [];
        const flatten = (categories, level = 0) => {
            categories.forEach(category => {
                flattenedRows.push({
                    ...category,
                    level,
                    hasChildren: category.children.length > 0,
                    isExpanded: expandedCategories.has(category.id)
                });
                
                if (category.children.length > 0 && expandedCategories.has(category.id)) {
                    flatten(category.children, level + 1);
                }
            });
        };
        
        flatten(rootCategories);
        return flattenedRows;
    };
    
    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 2,
            minWidth: 250,
            renderCell: (params) => {
                const indent = params.row?.level * 20 || 0;
                return (
                    <Box sx={{ ml: indent ? `${indent}px` : 0, display: 'flex', alignItems: 'center' }}>
                        {params.row?.hasChildren && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleExpandCollapse(params.row.id);
                                }}
                            >
                                {params.row?.isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                            </IconButton>
                        )}
                        <span>{params.row?.name || ''}</span>
                    </Box>
                );
            }
        },
        { 
            field: 'description',
            headerName: 'Description',
            flex: 2.5,
            minWidth: 300,
            valueGetter: (value, row) => {
                if (!row) return '-';
                return row.description || '-';
            }
        },
        {
            field: 'product_count',
            headerName: 'Products',
            flex: 0.8,
            minWidth: 100,
            type: 'number',
            align: 'center',
            headerAlign: 'center',
            valueGetter: (value, row) => {
                if (!row) return 0;
                return parseInt(row.product_count) || 0;
            },
            renderCell: (params) => (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    fontWeight: 'medium',
                    color: params.value > 0 ? 'primary.main' : 'text.secondary'
                }}>
                    {params.value}
                </Box>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            minWidth: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCategory(params.row);
                            setMenuAnchor(e.currentTarget);
                        }}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAddChild(params.row);
                        }}
                        title="Add Child Category"
                    >
                        <AddChildIcon />
                    </IconButton>
                </Box>
            )
        }
    ];
    
    const processedCategories = processCategories(categories);
    
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }
    
    return (
        <Box sx={{ width: '100%', p: 2 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" component="h2">
                                Category Management
                            </Typography>
                            <Box>
                                {categories.length > 0 && (
                                    <Button
                                        startIcon={<InventoryIcon />}
                                        onClick={handleAddProduct}
                                        sx={{ mr: 1 }}
                                    >
                                        Add Product
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleCategoryAction('add')}
                                >
                                    Add Category
                                </Button>
                            </Box>
                        </Box>
                        
                        {categories.length === 0 && !loading && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                You don't have any product categories yet. Please create at least one category.
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Search Categories"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by name or description"
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Filter Categories"
                                    value={filterParentOnly}
                                    onChange={(e) => setFilterParentOnly(e.target.value)}
                                >
                                    <MenuItem value={false}>All Categories</MenuItem>
                                    <MenuItem value={true}>Parent Categories Only</MenuItem>
                                </TextField>
                            </Box>
                        </Box>
                        
                        <Box sx={{ height: 500, width: '100%' }}>
                            <DataGrid
                                rows={processedCategories}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: { page: 0, pageSize: 10 }
                                    }
                                }}
                                pageSizeOptions={[5, 10, 20, 50]}
                                disableRowSelectionOnClick
                                loading={loading}
                                slots={{
                                    noRowsOverlay: () => (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <Typography>
                                                No categories found
                                            </Typography>
                                        </Box>
                                    )
                                }}
                                sx={{
                                    '& .MuiDataGrid-cell': {
                                        borderRight: '1px solid #e0e0e0',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        borderRight: '1px solid #e0e0e0',
                                    },
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-columnHeader:focus': {
                                        outline: 'none',
                                    }
                                }}
                            />
                        </Box>
            </Paper>
            
            {/* Category Dialog */}
            <CategoryDialog
                open={categoryDialog.open}
                category={categoryDialog.category}
                categories={categories}
                onClose={handleDialogClose}
                parent={categoryDialog.parent}
            />
            
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, category: null })}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Typography>
                        Are you sure you want to delete the category "{deleteDialog.category?.name}"?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This action cannot be undone. The category can only be deleted if it has no products or subcategories.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setDeleteDialog({ open: false, category: null });
                        setError(''); // Clear error when canceling
                    }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Actions Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => {
                    setMenuAnchor(null);
                    setSelectedCategory(null);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={() => handleCategoryAction('edit', selectedCategory)}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => {
                    setDeleteDialog({ open: true, category: selectedCategory });
                    setMenuAnchor(null);
                }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete
                </MenuItem>
            </Menu>
            
            {/* Success Notification */}
            <Snackbar 
                open={!!success} 
                autoHideDuration={4000} 
                onClose={() => setSuccess('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSuccess('')} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    {success}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CategoryManagement; 